import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { RefreshCw, MapPin, Play, Pause } from 'lucide-react';
import Map, { Marker, Popup } from 'react-map-gl';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-csp'; 
import 'mapbox-gl/dist/mapbox-gl.css';
import { BASE_URL } from './Settings/DASHBOARD_SETTINGS';
import { Button } from '../Components/Global/UI/Button';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_API_KEY || 'pk.eyJ1IjoiamFjZW1lZGljYWwiLCJhIjoiY21memRncHo1MDFmbDJyc2ZlZ242b3ZiZCJ9.XUPfWrdE_VtTwDd-x-leNw';

const Tracking = () => {
  const [trackingData, setTrackingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const mapRef = useRef();

  useEffect(() => {
    if (!mapboxgl.supported()) {
      toast.error('Your browser does not support WebGL. Map cannot be displayed.');
    }
    fetchTracking();
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchTracking, 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);

  useEffect(() => {
    filterData();
  }, [trackingData, searchTerm, statusFilter]);

  const fetchTracking = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/telematics/positions`);
      if (response.data.success) {
        const data = response.data.data.devices;
        setTrackingData(Array.isArray(data) ? data : []);
      } else {
        toast.error('Failed to fetch tracking data.');
      }
    } catch (error) {
      toast.error('Error fetching tracking data.');
      console.error('Error fetching tracking data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const filterData = () => {
    let filtered = trackingData;

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.vehicle_registration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.driver_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((item) => {
        const status =
          item.ignition === 'Y' && item.speed > 0
            ? 'active'
            : item.ignition === 'Y'
            ? 'stationary'
            : 'offline';
        return status === statusFilter;
      });
    }

    setFilteredData(filtered);
  };

  const handleVehicleClick = (vehicle) => {
    setSelectedVehicle(vehicle);
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [vehicle.lon, vehicle.lat],
        zoom: 15,
        duration: 2000,
      });
    }
  };

  const sortedVehicles = [...trackingData].sort((a, b) => {
    const getStatusPriority = (vehicle) => {
      if (vehicle.ignition === 'Y' && vehicle.speed > 0) return 1;
      if (vehicle.ignition === 'Y') return 2;
      return 3;
    };
    return getStatusPriority(a) - getStatusPriority(b);
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600">Loading tracking data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Tracking</h1>
              <p className="text-gray-600 mt-1">
                Real-time telematics data from Radius VelocityFleet
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setAutoRefresh(!autoRefresh)}
                variant={autoRefresh ? 'default' : 'outline'}
                className="flex items-center gap-2"
              >
                {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {autoRefresh ? 'Stop Auto' : 'Start Auto'}
              </Button>
              <Button
                onClick={fetchTracking}
                disabled={refreshing}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Vehicles ({sortedVehicles.length})
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {sortedVehicles.map((vehicle) => {
                  const status =
                    vehicle.ignition === 'Y' && vehicle.speed > 0
                      ? 'active'
                      : vehicle.ignition === 'Y'
                      ? 'stationary'
                      : 'offline';
                  const bgColor =
                    status === 'active'
                      ? 'bg-green-50 hover:bg-green-100'
                      : status === 'stationary'
                      ? 'bg-yellow-50 hover:bg-yellow-100'
                      : 'bg-gray-50 hover:bg-gray-100';

                  return (
                    <div
                      key={vehicle.id}
                      onClick={() => handleVehicleClick(vehicle)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${bgColor}`}
                    >
                      <div className="font-medium text-sm">{vehicle.vehicle_registration}</div>
                      <div className="text-xs text-gray-600">{vehicle.driver_name}</div>
                      <div className="text-xs text-gray-500 capitalize">{status}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Map View</h2>
              <div className="h-96">
                <Map
                  mapboxAccessToken={MAPBOX_TOKEN}
                  ref={mapRef}
                  initialViewState={{ longitude: -6.2603, latitude: 53.3498, zoom: 10 }}
                  mapStyle="mapbox://styles/mapbox/outdoors-v11"
                  onError={(err) => {
                    console.error('Mapbox error:', err);
                    toast.error(`Map error: ${err.message || 'Unknown error'}`);
                  }}
                  onLoad={() => console.log('Map loaded successfully')}
                  mapboxgl={mapboxgl}
                  style={{ width: '100%', height: '100%' }}
                >
                  {trackingData.map((vehicle) => {
                    const status =
                      vehicle.ignition === 'Y' && vehicle.speed > 0
                        ? 'active'
                        : vehicle.ignition === 'Y'
                        ? 'stationary'
                        : 'offline';
                    const iconColor =
                      status === 'active'
                        ? 'text-green-500'
                        : status === 'stationary'
                        ? 'text-yellow-500'
                        : 'text-gray-500';
                    return (
                      <Marker
                        key={vehicle.id}
                        longitude={vehicle.lon}
                        latitude={vehicle.lat}
                        anchor="bottom"
                        onClick={() => setSelectedVehicle(vehicle)}
                      >
                        <button
                          onClick={() => setSelectedVehicle(vehicle)}
                          className="border-none bg-transparent p-0"
                        >
                          <MapPin className={`w-6 h-6 ${iconColor} cursor-pointer`} />
                        </button>
                      </Marker>
                    );
                  })}

                  {selectedVehicle && (
                    <Popup
                      longitude={selectedVehicle.lon}
                      latitude={selectedVehicle.lat}
                      anchor="top"
                      onClose={() => setSelectedVehicle(null)}
                    >
                      <div>
                        <h3>{selectedVehicle.vehicle_registration}</h3>
                        <p>Driver: {selectedVehicle.driver_name}</p>
                        <p>Speed: {selectedVehicle.speed} km/h</p>
                        <p>
                          Status:{' '}
                          {selectedVehicle.ignition === 'Y' && selectedVehicle.speed > 0
                            ? 'Active'
                            : selectedVehicle.ignition === 'Y'
                            ? 'Stationary'
                            : 'Offline'}
                        </p>
                      </div>
                    </Popup>
                  )}
                </Map>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tracking;